import "./quickmenu.scss";
import { type FC, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useShopfrontDispatch, useShopfrontSelector } from "../../../_Bootstrap/hooks";
import { history } from "../../../_Bootstrap/RoutingAndStore";
import { showDialog } from "../../../Actions/Dialogs/DialogActions";
import { startLoading, stopLoading } from "../../../Actions/General/LayoutActions";
import { type QuickMenuType, updateQuickMenu } from "../../../Actions/Users/UserActions";
import Button from "../../../Common/Buttons/Button";
import PopupManager from "../../../Common/Popups/PopupManager";
import { determineDataSource } from "../../../Data/DataStores/DataSource";
import { usePermissions } from "../../../Utilities/Domain/Users/Permissions";
import FontAwesomeLight from "../../../Utilities/Platform/FontAwesome/FontAwesomeLight";
import FontAwesomeSolid from "../../../Utilities/Platform/FontAwesome/FontAwesomeSolid";
import Request from "../../../Utilities/Platform/Request";
import QuickMenuAdd from "./QuickMenuAdd";
import { QuickMenuItem } from "./QuickMenuItem";

const requiredPermissions = [ "modify_users" ] as const;
const defaultQuickMenu = [] as QuickMenuType;

const QuickMenu: FC<{
    visible: boolean;
}> = ({ visible }) => {
    const dispatch = useShopfrontDispatch();
    const user = useShopfrontSelector(state => state.user.id);
    const quickMenu = useShopfrontSelector(state => state.user.id ? state.user.quickMenu : defaultQuickMenu);
    const location = useLocation();

    const {
        modifyUsers,
    } = usePermissions(requiredPermissions);

    const [ currentRoute, setCurrentRoute ] = useState(-1);

    useEffect(() => {
        if(!quickMenu.length) {
            return;
        }

        setCurrentRoute(quickMenu.findIndex(item => item.url === location.pathname));
    }, [ quickMenu, location ]);

    const removeQuickMenuItem = () => {
        dispatch(startLoading());
        const item = quickMenu[currentRoute];

        if(item.id === -1 || !user) {
            // Ignore invalid
            return;
        }

        return Request.post("/graphql", {
            query: `
                mutation RemoveQuickMenuItem($user: ID!, $id: Int!) {
                    removeQuickMenuItem(user: $user, id: $id) {
                        id,
                        url,
                        name,
                        external
                    }
                }
            `,
            variables: {
                user: user,
                id  : item.id,
            },
        })
            .then(response => {
                if(response.data.error) {
                    throw response.data.error;
                }

                const quickMenu = response.data.data.removeQuickMenuItem;

                determineDataSource()
                    .then(source => {
                        return source.users.update(user, {
                            quick_menu: quickMenu,
                        });
                    })
                    .catch(err => {
                        console.error("Error updating local user's quick menu", err);
                    });

                PopupManager.success("Successfully removed quick menu item");
                dispatch(updateQuickMenu(quickMenu));
            })
            .catch(err => {
                PopupManager.error("Failed to remove quick menu item");
                console.error("Error removing quick menu item", err);
            })
            .then(() => {
                dispatch(stopLoading());
            });
    };

    let classes = "";

    if(visible) {
        classes = "visible";
    }

    const quickMenuUse = [ ...quickMenu ];

    if(!modifyUsers && quickMenuUse.length === 0) {
        quickMenuUse.push({
            id      : -1,
            url     : "/",
            external: false,
            name    : "Sell Screen",
        });
    }

    return (
        <div
            className={`quick-menu ${classes}`}
            inert={!visible}
        >
            <div className="quick-menu-arrow" />
            <ul>
                {modifyUsers && (
                    <li className="quick-menu-buttons">
                        <Button
                            onClick={e => {
                                e.stopPropagation();

                                if(currentRoute >= 0) {
                                    removeQuickMenuItem();
                                } else {
                                    dispatch(showDialog(
                                        "Add Quick Menu Item",
                                        <QuickMenuAdd url={location.pathname} />,
                                        "question"
                                    ));
                                }
                            }}
                            classes={[ (currentRoute >= 0 ? "current-route" : "add-route") ]}
                        >
                            {(currentRoute >= 0) ? (
                                <FontAwesomeSolid icon="star" />
                            ) : (
                                <FontAwesomeLight icon="star" />
                            )}
                        </Button>
                        <Button
                            onClick={e => {
                                e.stopPropagation();
                                history.push(`/settings/users/${user}/edit`);
                            }}
                            classes={[ "quick-menu-edit-button" ]}
                        >
                            <FontAwesomeLight icon="pencil" />
                        </Button>
                    </li>
                )}
                {quickMenuUse.map((quickMenu, index) => (
                    <QuickMenuItem
                        key={index}
                        href={quickMenu.url}
                        local={!quickMenu.external}
                        current={currentRoute === index}
                    >
                        {quickMenu.name}
                    </QuickMenuItem>
                ))}
            </ul>
        </div>
    );
};

export default QuickMenu;
